export function createPreSyncLogin(deps) {
  const {
    env = {},
    storage = {},
    config = {},
    sync = {},
    merge = {},
    ui = {},
    telemetry = {},
  } = deps || {};

  const { navigatorObj, windowObj, CLIENT_VERSION } = env;
  const { DB } = storage;
  const {
    GLOBAL_SYNC_FRESHNESS_MS,
    GLOBAL_BOOTSTRAP_PRIORITY,
    GLOBAL_BOOTSTRAP_SECONDARY,
    GLOBAL_KEY,
  } = config;
  const {
    pullGlobal,
    ensureServerInstall,
    mergeAuthAccounts,
  } = sync;
  const {
    buildCollectionIdentityKey,
    pickPreferredFarmRecord,
    dedupeCollectionForBootstrap,
    collectionItemsReferSameIdentity,
    choosePreferredCollectionItem,
  } = merge;
  const {
    setPreSyncing,
    setPreLoginStage,
    setPreLoginProgress,
    setLoginReady,
  } = ui;
  const { markBootTelemetry } = telemetry;

  const mergeBootstrapGlobal = async (gKey, options = {}) => {
    try {
      const freshnessMs = Number(options.freshnessMs || 0);
      if (freshnessMs > 0) {
        const warmedAt = Number((await DB.get(`${gKey}:_warmAt`)) || 0);
        if (warmedAt && (Date.now() - warmedAt) < freshnessMs) {
          return { key: gKey, skipped: true, fresh: true };
        }
      }
      const srv = await pullGlobal(gKey, "pre-login");
      const srvVer = Number(srv?.version || 0);
      const locVer = Number((await DB.get(`${gKey}:_ver`)) || 0);
      if (srv.data == null) {
        if (gKey === "auth:accounts") {
          try {
            const locData = await DB.get(gKey);
            if (!Array.isArray(locData) || locData.length === 0) {
              setTimeout(() => {
                mergeBootstrapGlobal("auth:accounts", { freshnessMs: 0 }).catch(() => {});
              }, 3000);
            }
          } catch (e) {}
        }
        return { key: gKey, skipped: true };
      }
      const srvArr = Array.isArray(srv.data) ? srv.data : [];
      const locData = await DB.get(gKey);
      const locArr = Array.isArray(locData) ? locData : [];
      const localEmpty = !Array.isArray(locData) || locArr.length === 0;
      const shouldHydrateWhenEmpty = localEmpty && srvArr.length > 0;
      if (!(srvVer > locVer) && !shouldHydrateWhenEmpty) {
        await DB.set(`${gKey}:_warmAt`, Date.now());
        return { key: gKey, skipped: true, sameVersion: true };
      }
      const collectionName = gKey === "fz:farms"
        ? "fz:farms"
        : (gKey === "auth:accounts" ? "auth:accounts" : String(gKey || "").replace(/^fz:global:/, ""));
      let merged;
      if (gKey === "auth:accounts") {
        merged = mergeAuthAccounts(srvArr, locArr);
      } else if (gKey === "fz:farms") {
        const byIdentity = new Map();
        [...srvArr, ...locArr].forEach((item) => {
          const key = buildCollectionIdentityKey(item, collectionName);
          const prev = byIdentity.get(key);
          byIdentity.set(key, prev ? pickPreferredFarmRecord(prev, item) : item);
        });
        merged = Array.from(byIdentity.values());
      } else {
        merged = dedupeCollectionForBootstrap([...(locArr || []), ...(srvArr || [])], collectionName).map((item) => {
          const localMatch = locArr.find((candidate) => collectionItemsReferSameIdentity(candidate, item, collectionName));
          const serverMatch = srvArr.find((candidate) => collectionItemsReferSameIdentity(candidate, item, collectionName));
          return choosePreferredCollectionItem(localMatch, serverMatch, collectionName);
        });
      }
      await DB.set(gKey, merged);
      await DB.set(`${gKey}:_ver`, srvVer);
      await DB.set(`${gKey}:_warmAt`, Date.now());
      windowObj.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: gKey } }));
      if (gKey === "fz:farms") windowObj.dispatchEvent(new CustomEvent("fz:farms:updated"));
      return { key: gKey, updated: true };
    } catch (e) {
      return { key: gKey, error: true };
    }
  };

  return async function preSyncLogin() {
    markBootTelemetry("prelogin-started");
    if (!navigatorObj.onLine) {
      markBootTelemetry("offline-ready", { source: "prelogin" });
      setPreLoginStage("offline-ready");
      setPreLoginProgress(100);
      setLoginReady(true);
      markBootTelemetry("prelogin-finished", { status: "offline-ready" });
      return;
    }

    try {
      localStorage.setItem("fz:appVersion", CLIENT_VERSION);
      try {
        for (const key of Object.keys(sessionStorage)) {
          if (
            key.startsWith("fz:technicalUpdateInProgress:") ||
            key.startsWith("fz:technicalUpdateIntent:") ||
            key.startsWith("fz:bypassBeforeUnloadUntil:") ||
            key.startsWith("fz:versionBoot:")
          ) {
            sessionStorage.removeItem(key);
          }
        }
        sessionStorage.removeItem(`fz:technicalUpdateIntent:${CLIENT_VERSION}`);
        sessionStorage.removeItem(`fz:technicalUpdateInProgress:${CLIENT_VERSION}`);
        sessionStorage.removeItem("fz:update:attempted");
        localStorage.removeItem("fz:update:attempted");
      } catch (_) {}
      localStorage.setItem("fz:lastPreLoginSyncAt", new Date().toISOString());
    } catch (e) {}

    setPreSyncing(true);
    setPreLoginStage("validating-install");
    markBootTelemetry("prelogin-install-check-started");
    setPreLoginProgress((prev) => Math.max(prev, 18));
    try {
      if (typeof ensureServerInstall === "function") {
        await ensureServerInstall("pre-login", { force: true });
        markBootTelemetry("prelogin-install-check-ready");
      }
    } catch (e) {
      markBootTelemetry("prelogin-install-check-interrupted", { message: String(e?.message || e || "") });
      setPreSyncing(false);
      return;
    }

    setPreLoginStage("pulling-global");
    markBootTelemetry("prelogin-global-started");
    setPreLoginProgress((prev) => Math.max(prev, 28));

    try {
      await Promise.allSettled(["fz:farms", "auth:accounts"].map((key) => mergeBootstrapGlobal(key, { freshnessMs: GLOBAL_SYNC_FRESHNESS_MS })));
      setLoginReady(true);
      markBootTelemetry("prelogin-global-ready");
      setPreLoginProgress((prev) => Math.max(prev, 58));
      setPreLoginStage("syncing-catalogs");
      markBootTelemetry("prelogin-catalogs-started");
      Promise.resolve().then(async () => {
        try {
          await Promise.allSettled(GLOBAL_BOOTSTRAP_PRIORITY.map((name) => mergeBootstrapGlobal(GLOBAL_KEY(name), { freshnessMs: GLOBAL_SYNC_FRESHNESS_MS })));
          markBootTelemetry("prelogin-catalogs-ready", { bucket: "priority" });
          setPreLoginProgress((prev) => Math.max(prev, 78));
          try { windowObj.dispatchEvent(new CustomEvent("fz:bootstrap-globals:priority-ready")); } catch (e) {}
          await Promise.allSettled(
            GLOBAL_BOOTSTRAP_SECONDARY.map((name, index) => new Promise((resolve) => {
              // v3.2.03: stagger aumentado — login já está visível neste ponto,
              // dados secundários podem esperar sem impacto visual.
              // Antes: 0/180/360ms. Agora: 4000/4600/5200ms.
              setTimeout(() => {
                Promise.resolve(mergeBootstrapGlobal(GLOBAL_KEY(name), { freshnessMs: GLOBAL_SYNC_FRESHNESS_MS }))
                  .then(resolve)
                  .catch(() => resolve(null));
              }, 4000 + index * 600);
            })),
          );
        } catch (e) {}
        try { windowObj.dispatchEvent(new CustomEvent("fz:bootstrap-globals:warmed")); } catch (e) {}
      });
    } catch (e) {}

    setPreSyncing(false);
    setPreLoginStage("done");
    setPreLoginProgress(100);
    markBootTelemetry("prelogin-finished", { status: "done" });

    // v3.2.07: removido fan-out redundante post-login-ready.
    // Os globais de prioridade e secundários já foram aquecidos acima com freshness/stagger;
    // repetir PRIORITY+SECONDARY com skipFresh:true recriava burst de pull_global no pré-login.
  };
}
